import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  MenuItem,
  Alert,
  Divider,
} from "@mui/material";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";

import Navbar from "../../../components/Homepage/Navbar/Navbar";
import Footer from "../../../components/Homepage/Footer/Footer";
import "./Login.css";

const API_URL = "http://localhost:5004";

const roles = [
  { value: "STUDENT", label: "Student" },
  { value: "HOD", label: "HOD / Department" },
  { value: "PRINCIPAL_OFFICE", label: "Principal Office" },
  { value: "LIBRARY", label: "Library" },
  { value: "HOSTEL", label: "Hostel Office" },
  { value: "ADMIN", label: "University Administrator" },
];

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();

  // Read the selected role from the URL.
  const queryRole = new URLSearchParams(location.search).get("role");

  const initialRole = roles.some((item) => item.value === queryRole)
    ? queryRole
    : "STUDENT";

  // Form state
  const [role, setRole] = useState(initialRole);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const selectedRole = roles.find((item) => item.value === role);

  const handleRoleChange = (event) => {
    const nextRole = event.target.value;

    setRole(nextRole);
    setError("");
    setSuccess("");

    navigate(
      `/student-erp/login?role=${encodeURIComponent(nextRole)}`,
      { replace: true }
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!username.trim() || !password) {
      setError("Please enter your username and password.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: username.trim(),
          password,
          role,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Login failed. Please try again.");
        return;
      }

      // Store the authenticated session data for this browser tab.
      sessionStorage.setItem("erpToken", data.token);
      sessionStorage.setItem("erpUser", JSON.stringify(data.user));
      

      setSuccess("Login successful. Redirecting...");

      const dashboardRoutes = {
        STUDENT: "/student-erp/student-dashboard",
        HOD: "/student-erp/hod-dashboard",
        PRINCIPAL_OFFICE: "/student-erp/principal-dashboard",
        LIBRARY: "/student-erp/library-dashboard",
        HOSTEL: "/student-erp/hostel-dashboard",
        ADMIN: "/student-erp/admin-dashboard",
      };

      navigate(dashboardRoutes[data.user.role] || "/student-erp");
    } catch (err) {
      console.error("Login request failed:", err);

      setError(
        "Cannot connect to the ERP server. Please check that the backend is running on port 5004."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <Box className="formal-login-page">
        <Container maxWidth="md" className="formal-login-container">
          <Button
            startIcon={<ArrowBackOutlinedIcon />}
            className="formal-back-button"
            onClick={() => navigate("/student-erp")}
          >
            Back to Student ERP
          </Button>

          <Paper className="formal-login-card" elevation={0}>
            <Box className="formal-university-header">
              <Box className="formal-university-emblem">
                <AccountBalanceOutlinedIcon />
              </Box>

              <Typography className="formal-university-name">
                RAYALASEEMA UNIVERSITY
              </Typography>

              <Typography className="formal-university-location">
                Kurnool, Andhra Pradesh
              </Typography>

              <Divider className="formal-header-divider" />

              <Typography
                component="h1"
                className="formal-portal-title"
              >
                Student ERP Portal
              </Typography>

              <Typography className="formal-portal-description">
                University Academic and Administrative Services
              </Typography>
            </Box>

            <Box className="formal-login-form-wrapper">
              <Typography
                component="h2"
                className="formal-form-title"
              >
                User Login
              </Typography>

              <Typography className="formal-form-description">
                Enter your registered credentials to continue.
              </Typography>

              {error && (
                <Alert
                  severity="error"
                  onClose={() => setError("")}
                  className="formal-login-alert"
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert
                  severity="success"
                  className="formal-login-alert"
                >
                  {success}
                </Alert>
              )}

              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                autoComplete="on"
              >
                <Typography
                  component="label"
                  htmlFor="formal-role"
                  className="formal-field-label"
                >
                  User Category
                </Typography>

                <TextField
                  id="formal-role"
                  select
                  fullWidth
                  value={role}
                  onChange={handleRoleChange}
                  className="formal-login-field"
                  margin="none"
                >
                  {roles.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Typography
                  component="label"
                  htmlFor="formal-username"
                  className="formal-field-label"
                >
                  Username
                </Typography>

                <TextField
                  id="formal-username"
                  name="username"
                  fullWidth
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="Enter username"
                  autoComplete="username"
                  className="formal-login-field"
                  margin="none"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography
                  component="label"
                  htmlFor="formal-password"
                  className="formal-field-label"
                >
                  Password
                </Typography>

                <TextField
                  id="formal-password"
                  name="password"
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter password"
                  autoComplete="current-password"
                  className="formal-login-field"
                  margin="none"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          type="button"
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() =>
                            setShowPassword((current) => !current)
                          }
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffOutlinedIcon />
                          ) : (
                            <VisibilityOutlinedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  className="formal-signin-button"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>

                <Typography className="formal-selected-role">
                  Signing in as: {selectedRole?.label || "Student"}
                </Typography>
              </Box>
            </Box>

            <Box className="formal-login-card-footer">
              <LockOutlinedIcon />
              <Typography>
                Authorized university users only
              </Typography>
            </Box>
          </Paper>

          <Typography className="formal-copyright">
            © {new Date().getFullYear()} Rayalaseema University. All rights
            reserved.
          </Typography>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
